import React, { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import Icon, { MaterialIcons } from '~/app/common/Icon';

import Trigger, { TriggerComponent } from './Trigger';
import Content, { ContentComponent } from './Content';

import classes from './style.module.scss';

type DropdownPosition =
  'auto' |
  'top left' |
  'top' |
  'top right' |
  'right' |
  'bottom right' |
  'bottom' |
  'bottom left' |
  'left'

type DropdownChildren = React.ReactElement<any, TriggerComponent | ContentComponent>

export interface DropdownRef {
  toggle: (state?: boolean) => void,
  open: () => void,
  close: () => void
}

export interface DropdownProps {
  ref?: React.Ref<DropdownRef>
  closeOnClick?: boolean
  label?: React.ReactNode
  icon?: MaterialIcons | null
  trigger?: React.ReactElement
  position?: DropdownPosition
  viewportOffset?: number
  children: DropdownChildren | DropdownChildren[]
  onOpen?: () => void
  onClose?: () => void
}

export interface DropdownComponent extends React.FC<DropdownProps> {
  Trigger: TriggerComponent
  Content: ContentComponent
}

// @ts-ignore
const Dropdown: DropdownComponent = React.forwardRef<DropdownRef, DropdownProps>((
  {
    closeOnClick = true,
    label,
    icon = 'arrow_drop_down',
    trigger: defaultTrigger,
    position: defaultPosition = 'auto',
    viewportOffset = 10,
    children,
    onOpen,
    onClose
  },
  ref
) => {
  const dropdownElRef = useRef<HTMLDivElement>(null);
  const dropdownContentElRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isShow, setIsShow] = useState<boolean>(false);
  const [position, setPosition] = useState<DropdownPosition>(defaultPosition);

  const windowClickHandler = useCallback(({ target }) => {
    if (dropdownElRef.current && !dropdownElRef.current.contains(target)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('click', windowClickHandler);
    } else {
      window.removeEventListener('click', windowClickHandler);
    }
  }, [isOpen, windowClickHandler]);

  useEffect(() => {
    if (isOpen && onOpen) {
      onOpen();
    }
    if (!isOpen && onClose) {
      onClose();
    }
  }, [isOpen, onClose, onOpen]);

  useLayoutEffect(() => {
    if (isOpen && dropdownContentElRef.current) {
      const boundingClientRect = dropdownContentElRef.current.getBoundingClientRect();

      let newPosition: string = position;

      const viewportOffsets = {
        top: boundingClientRect.top < viewportOffset,
        right: boundingClientRect.right > boundingClientRect.width - viewportOffset,
        bottom: boundingClientRect.bottom > boundingClientRect.height + viewportOffset,
        left: boundingClientRect.left < viewportOffset
      };

      if (position === 'auto') {
        if (viewportOffsets.top) {
          newPosition = 'bottom';
        } else if (viewportOffsets.right) {
          newPosition = 'left';
        } else if (viewportOffsets.bottom) {
          newPosition = 'top';
        } else if (viewportOffsets.left) {
          newPosition = 'right';
        } else {
          newPosition = 'bottom';
        }
      }

      if (newPosition === 'top' || newPosition === 'bottom') {
        if (viewportOffsets.left) {
          newPosition = newPosition + ' right';
        } else if (viewportOffsets.right) {
          newPosition = newPosition + ' left';
        }
      }

      if (newPosition === 'left' || newPosition === 'right') {
        if (viewportOffsets.top) {
          newPosition = 'bottom ' + newPosition;
        } else if (viewportOffsets.bottom) {
          newPosition = 'top ' + newPosition;
        }
      }

      setPosition(newPosition as DropdownPosition);
    }
  }, [isOpen, position, viewportOffset]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setIsShow(true);
      }, 50);
    } else {
      setIsShow(false);
    }
  }, [isOpen]);

  useImperativeHandle(ref, () => ({
    toggle: (state?: boolean) => (state !== undefined ? setIsOpen(state) : setIsOpen(!isOpen)),
    open: () => setIsOpen(true),
    close: () => setIsOpen(false)
  }), [isOpen]);

  const triggerClickHandler = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const contentClickHandler = useCallback(() => {
    if (closeOnClick) {
      setIsOpen(false);
    }
  }, [closeOnClick]);

  const childCount = React.Children.count(children);

  let trigger: React.ReactNode | undefined = defaultTrigger;
  let content: React.ReactNode = null;

  if (childCount > 1) {
    React.Children.forEach<DropdownChildren>(children, child => {
      if (child) {
        switch (child.type.displayName) {
          case 'Trigger':
            trigger = child;
            break;
          case 'Content':
            content = child;
            break;
        }
      }
    });
  } else if (childCount === 1) {
    content = React.Children.only(children);
  }

  return (
    <div ref={dropdownElRef} className={classes['dropdown']}>
      <span className={classes['dropdown-trigger']} onClick={triggerClickHandler}>
        {trigger || label}
        {!!icon && <Icon className={classes['dropdown-icon']} name={icon} />}
      </span>


      {isOpen && (
        <div
          ref={dropdownContentElRef}
          className={classNames(classes['dropdown-content'], { [classes['show']]: isShow }, position)}
          onClick={contentClickHandler}
        >
          {content}
        </div>
      )}
    </div>
  );
});

Dropdown.Trigger = Trigger;
Dropdown.Content = Content;

export default Dropdown
